import {
    Box,
    FormControlLabel,
    Snackbar,
    Switch,
    Typography
} from "@material-ui/core";
import { useContext, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { Edit } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import RegularButton from "components/CustomButtons/Button";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import { LoginContext } from "contexts/LoginContext";
import ProgramService from "services/ProgramServices/ProgramService";


const useStyles = makeStyles(styles);

const ProgramBatchModal = ({
    onClose,
    program,
    programId
}) => {
    const { user } = useContext(LoginContext);

    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        batchName: "",
        startDate: "",
        endDate: "",
        isActive: true
    });

    const [editingBatchId, setEditingBatchId] = useState(null);
    const [editData, setEditData] = useState({
        batchName: "",
        startDate: "",
        endDate: "",
        isActive: true
    });
    const [errors, setErrors] = useState({});
    const [open, setOpen] = useState(false);

    const [severity, setSeverity] = useState("success");
    const [alertMessage, setAlertMessage] = useState("");
    const classes = useStyles();
    useEffect(() => {
        fetchBatches();
    }, []);

    const fetchBatches = async () => {
        try {
            setLoading(true);
            const res = await ProgramService.getProgramBatches(program?.programId);
            setBatches(res?.data?.$values?.length > 0 ? res?.data?.$values : []);
        } catch (err) {
            console.error("Failed to fetch batches", err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData({ ...formData, [key]: value });
    };
    const showSnackBar = (message, sev = "success") => {
        setSeverity(sev);
        setAlertMessage(message);
        setOpen(true);
    };

    const handleClose = (event, reason) => {
        if (reason === "clickaway") return;
        setOpen(false);
        setAlertMessage(null);
    };
    const validate = () => {
        const err = {};

        if (!formData.batchName) err.batchName = "Batch name required";

        if (
            formData.startDate &&
            formData.endDate &&
            new Date(formData.endDate) <= new Date(formData.startDate)
        ) {
            err.endDate = "End date must be after start date";
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const startEdit = (batch) => {
        setEditingBatchId(batch.programBatchId);
        setEditData({
            batchName: batch.batchName,
            startDate: batch.startDate ? batch.startDate.split("T")[0] : "",
            endDate: batch.endDate ? batch.endDate.split("T")[0] : "",
            isActive: batch.isActive
        });
    };

    const cancelEdit = () => {
        setEditingBatchId(null);
        setEditData({
            batchName: "",
            startDate: "",
            endDate: "",
            isActive: true
        });
        fetchBatches();
    };

    const handleUpdateBatch = async () => {
        if (!editData.batchName) {
            showSnackBar("Batch name required", "error");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                batchName: editData.batchName,
                startDate: editData.startDate || null,
                endDate: editData.endDate || null,
                isActive: editData.isActive
            };

            await ProgramService.updateBatch(editingBatchId, payload);

            showSnackBar("Batch updated");

            // 🔥 update UI locally (no reload needed)
            const updated = batches.map((b) =>
                b.programBatchId === editingBatchId
                    ? { ...b, ...payload }
                    : b
            );

            setBatches(updated);
            cancelEdit();

        } catch (error) {
            showSnackBar("Update failed", "error");
        } finally {
            setLoading(false);
        }
    };
    const handleAddBatch = async () => {
        if (!validate()) return;

        try {
            setLoading(true);

            const payload = {
                batchName: formData.batchName,
                startDate: formData.startDate == "" ? null : formData.startDate,
                endDate: formData.endDate == "" ? null : formData.endDate,
                isActive: formData.isActive,
            };

            await ProgramService.addBatch(program?.programId, payload);

            // Reset form
            setFormData({
                batchName: "",
                startDate: "",
                endDate: "",
                isActive: true
            });
            onClose();
            fetchBatches();
        } catch (error) {
            console.log("FULL ERROR:", error);
            console.log("RESPONSE:", error?.response);
            console.log("DATA:", error?.response?.data);

            const errorMessage =
                error?.response?.data ||   // <-- your .NET BadRequest string
                error?.message ||
                "Failed to add batch.";

            showSnackBar(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader color="rose" icon>
                <CardIcon color="rose">
                    <i className="material-icons">groups</i>
                </CardIcon>
            </CardHeader>

            <CardBody>

                {/* ===== HEADER WITH ADD BUTTON ===== */}
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    // alignItems="center"
                    mb={3}
                >
                    {!showAddForm && (
                        <RegularButton
                            color="rose"
                            size="sm"
                            onClick={() => setShowAddForm(true)}
                            style={{ textTransform: "none" }}
                        >
                            + Add Batch
                        </RegularButton>
                    )}
                </Box>


                {/* ===== ADD BATCH FORM (ONLY WHEN TOGGLED) ===== */}
                {showAddForm && (
                    <Box
                        sx={{
                            p: 2,
                            mb: 4,
                            borderRadius: 2,
                            backgroundColor: "#f9fafb",
                            border: "1px solid #e0e0e0"
                        }}
                    >
                        <GridContainer spacing={2} alignItems="center">

                            <GridItem xs={12} sm={4}>
                                <CustomInput
                                    labelText="Batch Name *"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        value: formData.batchName,
                                        onChange: (e) =>
                                            handleChange("batchName", e.target.value)
                                    }}
                                    error={!!errors.batchName}
                                    helperText={errors.batchName}
                                />
                            </GridItem>

                            <GridItem xs={12} sm={3}>
                                <CustomInput
                                    labelText="Start"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: "date",
                                        value: formData.startDate,
                                        onChange: (e) =>
                                            handleChange("startDate", e.target.value)
                                    }}
                                />
                            </GridItem>

                            <GridItem xs={12} sm={3}>
                                <CustomInput
                                    labelText="End"
                                    formControlProps={{ fullWidth: true }}
                                    inputProps={{
                                        type: "date",
                                        value: formData.endDate,
                                        onChange: (e) =>
                                            handleChange("endDate", e.target.value)
                                    }}
                                />
                            </GridItem>

                            <GridItem xs={12} sm={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData?.isActive}
                                            onChange={(e) =>
                                                handleChange("isActive", e.target.checked)
                                            }
                                        />
                                    }
                                    label="Active"
                                />
                            </GridItem>
                            <GridItem xs={12}>
                                <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={4}
                                    mt={1}
                                >
                                    <RegularButton
                                        color="default"
                                        onClick={() => setShowAddForm(false)}
                                        style={{ textTransform: "none", marginRight: 5 }}
                                    >
                                        Cancel
                                    </RegularButton>

                                    <RegularButton
                                        color="rose"
                                        onClick={handleAddBatch}
                                        disabled={loading}
                                        style={{ textTransform: "none" }}
                                    >
                                        {loading ? "Saving..." : "Save"}
                                    </RegularButton>
                                </Box>
                            </GridItem>
                        </GridContainer>
                    </Box>
                )}


                {/* ===== BATCH LIST ===== */}
                {batches.length === 0 ? (
                    <Typography color="text.secondary">
                        No batches added yet.
                    </Typography>
                ) : (
                    batches.map((batch) => {
                        const isEditing = editingBatchId === batch.programBatchId;

                        return (
                            <Box
                                key={batch.programBatchId}
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    backgroundColor: "#fafafa",
                                    border: "1px solid #e0e0e0"
                                }}
                            >
                                {isEditing ? (
                                    <GridContainer spacing={2} alignItems="center">

                                        <GridItem xs={12} sm={4}>
                                            <CustomInput
                                                labelText="Batch Name *"
                                                formControlProps={{ fullWidth: true }}
                                                inputProps={{
                                                    value: editData.batchName,
                                                    onChange: (e) =>
                                                        setEditData({ ...editData, batchName: e.target.value })
                                                }}
                                            />
                                        </GridItem>

                                        <GridItem xs={12} sm={3}>
                                            <CustomInput
                                                labelText="Start"
                                                formControlProps={{ fullWidth: true }}
                                                inputProps={{
                                                    type: "date",
                                                    value: editData.startDate,
                                                    onChange: (e) =>
                                                        setEditData({ ...editData, startDate: e.target.value })
                                                }}
                                            />
                                        </GridItem>

                                        <GridItem xs={12} sm={3}>
                                            <CustomInput
                                                labelText="End"
                                                formControlProps={{ fullWidth: true }}
                                                inputProps={{
                                                    type: "date",
                                                    value: editData.endDate,
                                                    onChange: (e) =>
                                                        setEditData({ ...editData, endDate: e.target.value })
                                                }}
                                            />
                                        </GridItem>

                                        <GridItem xs={12} sm={2}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={editData.isActive}
                                                        onChange={(e) =>
                                                            setEditData({
                                                                ...editData,
                                                                isActive: e.target.checked
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Active"
                                            />
                                        </GridItem>

                                        {/* ACTION BUTTONS */}
                                        <GridItem xs={12}>
                                            <Box display="flex" justifyContent="flex-end" gap={2}>
                                                <RegularButton
                                                    color="default"
                                                    onClick={cancelEdit}
                                                    style={{ textTransform: "none" }}
                                                >
                                                    Cancel
                                                </RegularButton>

                                                <RegularButton
                                                    color="rose"
                                                    onClick={handleUpdateBatch}
                                                    disabled={loading}
                                                    style={{ textTransform: "none" }}
                                                >
                                                    {loading ? "Saving..." : "Save"}
                                                </RegularButton>
                                            </Box>
                                        </GridItem>
                                    </GridContainer>
                                ) : (
                                    <GridContainer alignItems="center">

                                        <GridItem xs={12} sm={3}>
                                            <Typography fontWeight={600}>
                                                {batch.batchName}
                                            </Typography>
                                        </GridItem>

                                        <GridItem xs={12} sm={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                Start Date
                                            </Typography>
                                            <Typography>
                                                {batch.startDate
                                                    ? new Date(batch.startDate).toLocaleDateString()
                                                    : "-"}
                                            </Typography>
                                        </GridItem>

                                        <GridItem xs={12} sm={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                End Date
                                            </Typography>
                                            <Typography>
                                                {batch.endDate
                                                    ? new Date(batch.endDate).toLocaleDateString()
                                                    : "-"}
                                            </Typography>
                                        </GridItem>

                                        <GridItem xs={12} sm={2}>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: 10,
                                                    display: "inline-block",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    backgroundColor: batch.isActive
                                                        ? "#e8f5e9"
                                                        : "#ffebee",
                                                    color: batch.isActive
                                                        ? "#2e7d32"
                                                        : "#c62828"
                                                }}
                                            >
                                                {batch.isActive ? "Active" : "Inactive"}
                                            </Box>
                                        </GridItem>

                                        <GridItem xs={12} sm={1}>
                                            <Button
                                                size="sm"
                                                justIcon
                                                round
                                                className={classes.actionButton}
                                                style={{ backgroundColor: "#DD4B39" }}
                                                onClick={() => startEdit(batch)}
                                            >
                                                <Edit />
                                            </Button>
                                        </GridItem>

                                    </GridContainer>
                                )}
                            </Box>
                        );
                    })
                )}

            </CardBody>
            <Snackbar
                open={open}
                autoHideDuration={2000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleClose} severity={severity}>
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default ProgramBatchModal;